import { useState, useRef, useEffect, FormEvent } from 'react';

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d7ajrspnauq9gnuqnk50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const timeline = [
    { num: '1', label: 'Submit form' },
    { num: '2', label: 'Engineer reviews' },
    { num: '3', label: 'Inspection scheduled' },
    { num: '4', label: 'Recommendation delivered' },
  ];

  const propertyTypes = [
    'Parking Garage',
    'High-Rise Building',
    'Tunnel',
    'Elevator Pit',
    'Podium Slab',
    'Swimming Pool',
    'Sea Wall',
    'Basement',
    'Other',
  ];

  return (
    <section id="contact" className="bg-[#0a0e1a] py-20 md:py-28 relative overflow-hidden">
      {/* Background photo */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://clwizard.com/wp-content/uploads/2024/03/IMG_0599_Edit2b.jpg"
          alt="CLWizard team at work"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e1a] via-[#0a0e1a]/80 to-[#0a0e1a]"></div>
      </div>

      <div
        ref={ref}
        className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 opacity-0 translate-y-8 transition-all duration-700 ease-out"
      >
        <div className="text-center mb-14">
          <span className="inline-block bg-[#0055ff]/10 border border-[#0055ff]/30 text-[#4d8fff] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            Free Assessment
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Protect Your Structure.<br />Eliminate Recurring Leaks.
          </h2>
          <p className="text-gray-400 text-lg">Talk to an engineer today.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Form */}
          <div className="bg-[#060912] border border-white/10 rounded-lg p-5 md:p-8">
            {submitted ? (
              <div className="text-center py-12">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-5 bg-[#0055ff]/20 rounded-full text-[#0055ff] text-3xl">
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
                <h3 className="text-white font-black text-2xl mb-3">Assessment Requested!</h3>
                <p className="text-gray-400 text-base">
                  Our engineering team will review your request and contact you within 24 hours to schedule your free inspection.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="clwizard-assessment-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#0055ff]/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#0055ff]/60 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(561) 000-0000"
                      className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#0055ff]/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                      Property Type *
                    </label>
                    <select
                      name="property_type"
                      required
                      className="w-full bg-[#0a0e1a] border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#0055ff]/60 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">Select type...</option>
                      {propertyTypes.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Describe the Issue
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe the water intrusion issue, location, and how long it has been occurring..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#0055ff]/60 transition-colors resize-none"
                  />
                  <p className="text-gray-600 text-xs text-right mt-1">{charCount}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0055ff] hover:bg-[#0044cc] disabled:opacity-60 text-white font-bold py-4 rounded-md text-sm uppercase tracking-wider transition-all duration-200"
                >
                  {submitting ? 'Sending...' : 'Request My Free Engineering Assessment'}
                </button>
              </form>
            )}
          </div>

          {/* Right side info */}
          <div className="space-y-6 md:space-y-8">
            {/* Timeline */}
            <div>
              <h3 className="text-white font-bold text-lg mb-5">What Happens Next</h3>
              <div className="space-y-4">
                {timeline.map((t, i) => (
                  <div key={t.num} className="flex items-center gap-4">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 bg-[#0055ff] text-white font-black text-sm rounded-full">
                      {t.num}
                    </div>
                    {i < timeline.length - 1 && (
                      <div className="absolute ml-4 mt-8 w-0.5 h-4 bg-[#0055ff]/30"></div>
                    )}
                    <span className="text-gray-300 text-sm font-medium">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact info */}
            <div className="bg-[#060912] border border-white/8 rounded-lg p-6 space-y-4">
              <h3 className="text-white font-bold text-base mb-4">Contact Us Directly</h3>
              <a
                href="tel:5617745120"
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
              >
                <span className="w-9 h-9 flex items-center justify-center bg-[#0055ff]/10 rounded-full text-[#0055ff] group-hover:bg-[#0055ff]/20 transition-colors">
                  <i className="ri-phone-fill"></i>
                </span>
                <span className="font-semibold text-sm">(561) 774-5120</span>
              </a>
              <a
                href="mailto:info@clwizard.com"
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
              >
                <span className="w-9 h-9 flex items-center justify-center bg-[#0055ff]/10 rounded-full text-[#0055ff] group-hover:bg-[#0055ff]/20 transition-colors">
                  <i className="ri-mail-fill"></i>
                </span>
                <span className="font-semibold text-sm">info@clwizard.com</span>
              </a>
            </div>

            {/* Final CTA */}
            <div className="bg-[#0055ff]/10 border border-[#0055ff]/30 rounded-lg p-5 md:p-6 text-center">
              <p className="text-white font-bold text-sm md:text-base mb-4">
                &ldquo;Protect Your Structure. Eliminate Recurring Leaks. Talk to an engineer today.&rdquo;
              </p>
              <button
                onClick={() => document.getElementById('clwizard-assessment-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#0055ff] hover:bg-[#0044cc] text-white font-bold px-6 py-3 rounded-md text-sm uppercase tracking-wider transition-all duration-200 w-full"
              >
                Schedule My Free Inspection
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
